/**
 * Klaviyo Reviews – grid / masonry helpers.
 *
 * Loaded synchronously by sections/apps.liquid (before the Klaviyo app block renders) when
 * "Klaviyo Reviews – Grid-Layout" is enabled. Works together with assets/klaviyo-reviews-grid.css.
 *
 * 0. Packs: a product with the metafield custom.bewertungen_von (section attribute data-review-sources) shows the
 *    reviews of those products next to its own. Its list requests are answered with one merged, re-sorted page
 *    (each borrowed review's variant line names its product: "Hydron · Lemon / 12 x 52g"), and the star widgets'
 *    summary request gets the combined rating and count.
 * 1. Photos first: Klaviyo's widget loads review pages with fetch() from
 *    /reviews/api/client_reviews/<product_id>/?…&offset=N&media=false. For the unfiltered first page we fetch the
 *    same endpoint with media=true (the product's reviews with photos/videos), prepend them, and drop those ids
 *    from later pages so nothing is shown twice. Searched / filtered requests pass through untouched, so Klaviyo
 *    keeps rendering the cards itself (markup, lightbox, translations).
 *    The number of photo reviews comes from data-photos-first on the section container (0 = off).
 * 2. Masonry: the review list is a CSS grid with 4px auto rows; every card gets a `grid-row-end: span N` based on
 *    its rendered height (ResizeObserver keeps it in sync when images load or the viewport changes).
 * 3. Histogram: Klaviyo only prints percentages ("87%"). The absolute count is available in the bar's aria-label
 *    ("544 Bewertungen mit 5 Sternen"), so we copy it into a data attribute that the CSS renders via ::after.
 * 4. Carousel (section class klaviyo-reviews-grid--carousel): one row of cards that drifts slowly on its own
 *    (pauses on hover / touch / wheel / focus, off with prefers-reduced-motion, loops back at the very end); the
 *    arrows scroll by the visible width, and Klaviyo's hidden "Mehr anzeigen" button is clicked near the end.
 *
 * 5. Read more (10.10.2026, feedback Navid): the review text is clamped (6 lines, 5 on phones, 3 in the carousel).
 *    Cards whose text is really cut off get a "Mehr anzeigen" / "Weniger anzeigen" toggle (button.kl-review-toggle,
 *    appended to the card); the expanded state is the attribute data-kl-expanded on the card, the CSS removes the
 *    clamp from it. Reviews that arrive later ("Mehr anzeigen" pagination, search / filter) are picked up by the same
 *    MutationObserver; a ResizeObserver on the texts re-checks after resizes (column width changes the line count).
 *
 * Klaviyo renders the widget with React. To stay compatible we never touch its text nodes or child lists – we only
 * add attributes / inline styles, and re-apply them whenever React re-renders (MutationObserver). The one exception
 * is the read-more button, a foreign child at the end of the card element that React ignores.
 */
(function () {
  'use strict';

  const ROW_UNIT = 4; // must match grid-auto-rows in klaviyo-reviews-grid.css
  const SECTION_SELECTOR = '.klaviyo-reviews-grid';
  const WRAPPER_SELECTOR = '#klaviyo-product-reviews-wrapper';
  const ITEM_SELECTOR = '.kl_reviews__review_item';
  const REVIEWS_ENDPOINT = /\/reviews\/api\/client_reviews\/(\d+)\/?$/;
  const SUMMARY_ENDPOINT = /\/reviews\/api\/client_reviews\/?$/;

  /* ------------------------------------------------------------------------------------------------------------
     0. Packs: reviews of the contained products (fetch interception)
     ------------------------------------------------------------------------------------------------------------ */

  function reviewSources(productId) {
    const section = document.querySelector(SECTION_SELECTOR + '[data-review-sources][data-product-id="' + productId + '"]');
    return section ? section.dataset.reviewSources.split(',').filter(Boolean) : [];
  }

  function fetchJson(fetchFn, href) {
    return fetchFn(href)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }

  function jsonResponse(data) {
    return new Response(JSON.stringify(data), { status: 200, headers: { 'Content-Type': 'application/json' } });
  }

  // review_count-weighted star rating of several Klaviyo product records
  function combineRatings(records) {
    let count = 0;
    let sum = 0;
    records.forEach((record) => {
      const reviews = Number(record.review_count) || 0;
      if (!reviews || record.star_rating == null) return;
      count += reviews;
      sum += Number(record.star_rating) * reviews;
    });
    return { review_count: count, star_rating: count ? Math.round((sum / count) * 100) / 100 : null };
  }

  // Search, variant filter and single-review deep links stay the pack's own business.
  function describePackRequest(rawUrl) {
    let url;
    try {
      url = new URL(rawUrl, location.href);
    } catch (error) {
      return null;
    }

    const match = url.pathname.match(REVIEWS_ENDPOINT);
    if (match) {
      const params = url.searchParams;
      if ((params.get('type') || 'reviews') !== 'reviews') return null;
      if (params.get('filter') || params.get('variant_id') || params.get('kl_review_uuid')) return null;
      const sources = reviewSources(match[1]);
      return sources.length ? { kind: 'list', url, productId: match[1], sources } : null;
    }

    if (SUMMARY_ENDPOINT.test(url.pathname)) {
      let ids = [];
      try {
        ids = JSON.parse(url.searchParams.get('products') || '[]').map(String);
      } catch (error) {
        return null;
      }
      const packs = new Map();
      ids.forEach((id) => {
        const sources = reviewSources(id);
        if (sources.length) packs.set(id, sources);
      });
      return packs.size ? { kind: 'summary', url, packs } : null;
    }

    return null;
  }

  // One page of the pack's list: every product (pack + sources) is asked for the whole window from offset 0, the
  // reviews are merged and sorted like Klaviyo would (sort 1 = best, 2 = worst, otherwise newest) and sliced again.
  async function fetchPackList(nativeFetch, request) {
    const params = request.url.searchParams;
    const offset = parseInt(params.get('offset') || '0', 10) || 0;
    const limit = parseInt(params.get('limit') || '5', 10) || 5;

    const windowUrl = (productId) => {
      const url = new URL(request.url.href);
      url.pathname = url.pathname.replace(REVIEWS_ENDPOINT, '/reviews/api/client_reviews/' + productId + '/');
      url.searchParams.set('product_id', productId);
      url.searchParams.set('offset', '0');
      url.searchParams.set('limit', String(offset + limit));
      return url.href;
    };

    const [own, ...parts] = await Promise.all(
      [request.productId, ...request.sources].map((id) => fetchJson(nativeFetch, windowUrl(id)))
    );
    if (!own || !Array.isArray(own.reviews)) return null;

    const sources = parts.filter((part) => part && Array.isArray(part.reviews));
    // Klaviyo prints "Variante: <title>" from the matching entry of review.product.variants
    sources.forEach((part) => {
      const name = String((part.product && part.product.name) || '').split(':')[0].trim();
      const label = (title) => (title && title !== 'Default Title' ? name + ' · ' + title : name);
      part.reviews.forEach((review) => {
        if (!review.product || !Array.isArray(review.product.variants)) return;
        review.product.variants = review.product.variants.map((variant) => ({ ...variant, title: label(variant.title) }));
      });
    });

    const all = [own, ...sources];
    const byDate = (a, b) => String(b.created_at).localeCompare(String(a.created_at));
    const sort = params.get('sort');
    const reviews = all
      .flatMap((data) => data.reviews)
      .sort((a, b) => {
        if (sort === '1') return Number(b.rating) - Number(a.rating) || byDate(a, b);
        if (sort === '2') return Number(a.rating) - Number(b.rating) || byDate(a, b);
        return byDate(a, b);
      });
    const total = all.reduce((sum, data) => sum + (Number(data.filtered_count) || data.reviews.length), 0);

    own.reviews = reviews.slice(offset, offset + limit);
    own.filtered_count = total;
    own.has_more = offset + limit < total;
    own.variant_counts = all.flatMap((data) => data.variant_counts || []);

    // summary (average, count, histogram, photo strip) over all products
    ['product', 'summary'].forEach((key) => {
      if (!own[key]) return;
      const records = all.map((data) => data[key]).filter(Boolean);
      Object.assign(own[key], combineRatings(records));
      own[key].rating_histogram = [0, 1, 2, 3, 4].map((i) =>
        records.reduce((sum, record) => sum + ((record.rating_histogram || [])[i] || 0), 0)
      );
      own[key].review_images = records.flatMap((record) => record.review_images || []);
    });

    return own;
  }

  // Star widgets ask for many products at once – the packs among them get the combined rating and count.
  async function fetchPackSummary(nativeFetch, request) {
    const sourceIds = [...new Set([...request.packs.values()].flat())];
    const sourceUrl = new URL(request.url.href);
    sourceUrl.searchParams.set('products', JSON.stringify(sourceIds));

    const [own, sources] = await Promise.all([
      fetchJson(nativeFetch, request.url.href),
      fetchJson(nativeFetch, sourceUrl.href),
    ]);
    if (!own || !Array.isArray(own.products)) return null;

    const byId = new Map(((sources && sources.products) || []).map((record) => [String(record.shopify_id), record]));
    own.products.forEach((record) => {
      const packSources = request.packs.get(String(record.shopify_id));
      if (!packSources) return;
      const records = [record, ...packSources.map((id) => byId.get(id)).filter(Boolean)];
      Object.assign(record, combineRatings(records));
    });

    return own;
  }

  /* ------------------------------------------------------------------------------------------------------------
     1. Photos first (fetch interception)
     ------------------------------------------------------------------------------------------------------------ */

  const surfacedMediaIds = new Map(); // productId -> Set<reviewId> already shown at the top
  const mediaRequests = new Map(); // cache key -> Promise<Array<review>>

  function photosFirstCount(productId) {
    const sections = document.querySelectorAll(SECTION_SELECTOR + '[data-photos-first]');
    for (const section of sections) {
      if (!section.dataset.productId || section.dataset.productId === productId) {
        return parseInt(section.dataset.photosFirst, 10) || 0;
      }
    }
    return 0;
  }

  // Only the plain, unfiltered review list is merged. Anything with a search term, variant filter, media filter
  // or a single-review deep link is Klaviyo's business.
  function describeRequest(rawUrl) {
    let url;
    try {
      url = new URL(rawUrl, location.href);
    } catch (error) {
      return null;
    }

    const match = url.pathname.match(REVIEWS_ENDPOINT);
    if (!match) return null;

    const params = url.searchParams;
    if ((params.get('type') || 'reviews') !== 'reviews') return null;
    if (params.get('media') === 'true') return null;
    if (params.get('filter')) return null;
    if (params.get('variant_id')) return null;
    if (params.get('kl_review_uuid')) return null;

    const offset = parseInt(params.get('offset') || '0', 10);
    if (Number.isNaN(offset)) return null;

    return { productId: match[1], offset, url };
  }

  function fetchMediaReviews(fetchReviews, request, count) {
    const key = request.productId + '|' + (request.url.searchParams.get('sort') || '') + '|' + count;

    if (!mediaRequests.has(key)) {
      const url = new URL(request.url.href);
      url.searchParams.set('media', 'true');
      url.searchParams.set('limit', String(count));
      url.searchParams.set('offset', '0');

      const promise = fetchReviews(url.href)
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => (data && Array.isArray(data.reviews) ? data.reviews : []))
        .catch(() => []);

      mediaRequests.set(key, promise);
      promise.then((list) => {
        if (!list.length) mediaRequests.delete(key); // nothing cached on error / no media – retry next time
      });
    }

    return mediaRequests.get(key);
  }

  async function mergeResponse(fetchReviews, request, responsePromise, count) {
    const mediaPromise = request.offset === 0 ? fetchMediaReviews(fetchReviews, request, count) : null;
    const response = await responsePromise;
    if (!response || !response.ok) return response;

    let data;
    try {
      data = await response.clone().json();
    } catch (error) {
      return response;
    }
    if (!data || !Array.isArray(data.reviews)) return response;

    if (mediaPromise) {
      const media = await mediaPromise;
      const ids = new Set(media.map((review) => String(review.id)));
      surfacedMediaIds.set(request.productId, ids);
      if (!media.length) return response;
      data.reviews = media.concat(data.reviews.filter((review) => !ids.has(String(review.id))));
    } else {
      const ids = surfacedMediaIds.get(request.productId);
      if (!ids || !ids.size) return response;
      data.reviews = data.reviews.filter((review) => !ids.has(String(review.id)));
    }

    return new Response(JSON.stringify(data), {
      status: response.status,
      statusText: response.statusText,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  function installFetchHooks() {
    if (window.__klaviyoReviewsGridFetch || typeof window.fetch !== 'function') return;

    const nativeFetch = window.fetch.bind(window);
    window.__klaviyoReviewsGridFetch = nativeFetch;

    const urlOf = (input) =>
      typeof input === 'string' ? input : input && typeof input.url === 'string' ? input.url : String(input);

    // 0. a pack's requests are answered with the merged data – anything that goes wrong falls back to Klaviyo's own
    const fetchReviews = (input, init) => {
      const pack = describePackRequest(urlOf(input));
      if (!pack) return nativeFetch(input, init);
      const merged = pack.kind === 'list' ? fetchPackList(nativeFetch, pack) : fetchPackSummary(nativeFetch, pack);
      return merged.then(
        (data) => (data ? jsonResponse(data) : nativeFetch(input, init)),
        () => nativeFetch(input, init)
      );
    };

    window.fetch = function (input, init) {
      const request = describeRequest(urlOf(input));
      const count = request ? photosFirstCount(request.productId) : 0;
      const responsePromise = fetchReviews(input, init);

      if (!request || !count) return responsePromise;
      return mergeResponse(fetchReviews, request, responsePromise, count).catch(() => responsePromise);
    };
  }

  /* ------------------------------------------------------------------------------------------------------------
     2. + 3. Masonry spans and histogram counts
     ------------------------------------------------------------------------------------------------------------ */

  class KlaviyoReviewsGrid {
    constructor(section) {
      this.section = section;
      this.pending = new Set();
      this.observedCards = new WeakSet();
      this.frame = 0;

      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const item = entry.target.parentElement;
          if (item) this.pending.add(item);
        }
        this.schedule();
      });

      this.observedTexts = new WeakSet();
      this.textObserver = new ResizeObserver((entries) => {
        for (const entry of entries) this.syncReadMoreFor(entry.target);
      });
      section.addEventListener('click', (event) => this.onReadMoreClick(event));
      // web fonts change the line breaks without changing the clamped box size, so measure again once they are in
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.syncReadMore());
      window.addEventListener('load', () => this.syncReadMore());

      this.isCarousel = section.classList.contains('klaviyo-reviews-grid--carousel');
      this.onRowScroll = () => this.updateCarousel();
      if (this.isCarousel) {
        this.bindCarouselNav();
        this.setupAutoScroll();
      }

      this.mutationObserver = new MutationObserver(() => this.refresh());
      this.mutationObserver.observe(section, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-label'],
      });

      this.refresh();
    }

    refresh() {
      const wrapper = this.section.querySelector(WRAPPER_SELECTOR);
      if (!wrapper) return;

      this.applyHistogramCounts(wrapper);
      this.upgradeCardImages(wrapper);
      this.syncReadMore(wrapper);

      if (this.isCarousel) {
        this.attachCarousel(wrapper);
        return; // no masonry spans in a single row
      }

      wrapper.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
        const card = item.firstElementChild;
        if (!card) return;

        if (!this.observedCards.has(card)) {
          this.observedCards.add(card);
          this.resizeObserver.observe(card);
        }

        this.pending.add(item);
      });

      this.schedule();
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.flush();
      });
    }

    flush() {
      const items = Array.from(this.pending).filter((item) => item.isConnected && item.firstElementChild);
      this.pending.clear();
      if (items.length === 0) return;

      const grid = items[0].parentElement;
      const gap = grid ? parseFloat(getComputedStyle(grid).columnGap) || 0 : 0;

      // Read all heights first, then write all spans, to avoid layout thrashing.
      const spans = items.map((item) => {
        const height = item.firstElementChild.getBoundingClientRect().height;
        return Math.max(1, Math.ceil((height + gap) / ROW_UNIT));
      });

      items.forEach((item, index) => {
        const value = 'span ' + spans[index];
        if (item.style.gridRowEnd !== value) item.style.gridRowEnd = value;
      });

      // Only switch the grid into masonry mode once spans exist, otherwise cards would collapse for a frame.
      this.section.classList.add('is-masonry');
    }

    /* ---- Carousel ---------------------------------------------------------------------------------------------- */

    bindCarouselNav() {
      this.nav = this.section.querySelector('[data-kl-carousel-nav]');
      if (!this.nav) return;
      this.nav.addEventListener('click', (event) => {
        const button = event.target.closest('[data-kl-carousel-prev], [data-kl-carousel-next]');
        if (!button || !this.row) return;
        this.holdAuto(5000);
        const direction = button.hasAttribute('data-kl-carousel-prev') ? -1 : 1;
        const step = Math.max(this.row.clientWidth * 0.8, 240);
        this.row.scrollBy({ left: direction * step, behavior: 'smooth' });
      });
    }

    attachCarousel(wrapper) {
      const item = wrapper.querySelector(ITEM_SELECTOR);
      const row = item ? item.parentElement : null;
      if (row !== this.row) {
        if (this.row) this.row.removeEventListener('scroll', this.onRowScroll);
        this.row = row;
        if (row) {
          row.addEventListener('scroll', this.onRowScroll, { passive: true });
          this.syncSnapPadding();
          row.scrollLeft = 0;
        }
      }
      this.positionNav();
      if (!this.onResize) {
        this.onResize = () => {
          this.syncSnapPadding();
          this.positionNav();
        };
        window.addEventListener('resize', this.onResize, { passive: true });
      }
      // unlock "load more" once new cards have arrived (or after a timeout if the request failed)
      const count = wrapper.querySelectorAll(ITEM_SELECTOR).length;
      if (count !== this.itemCount) {
        this.itemCount = count;
        this.loading = false;
      }
      this.updateCarousel();
    }

    // Slow continuous drift. scrollLeft is written from a float accumulator (browsers round it to whole pixels),
    // scroll snapping is switched off while drifting (class is-auto) and only comes back after a touch.
    setupAutoScroll() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.autoSpeed = 28; // px per second
      this.pausedUntil = 0;
      this.hovering = false;
      this.visible = false;
      this.tick = (time) => this.autoStep(time);

      new IntersectionObserver(
        (entries) => {
          this.visible = entries[0].isIntersecting;
          this.lastTime = 0;
          if (this.visible && !this.raf) this.raf = requestAnimationFrame(this.tick);
        },
        { threshold: 0.15 }
      ).observe(this.section);

      this.section.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.hovering = true;
      });
      this.section.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.hovering = false;
      });
      this.section.addEventListener(
        'touchstart',
        () => {
          this.holdAuto(4000);
          this.section.classList.remove('is-auto'); // let the finger snap cards again
        },
        { passive: true }
      );
      this.section.addEventListener(
        'wheel',
        (event) => {
          if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) this.holdAuto(4000);
        },
        { passive: true }
      );
      this.section.addEventListener('focusin', () => this.holdAuto(8000));
    }

    holdAuto(ms) {
      this.pausedUntil = Math.max(this.pausedUntil || 0, performance.now() + ms);
    }

    autoStep(time) {
      this.raf = 0;
      if (!this.visible) return;
      this.raf = requestAnimationFrame(this.tick);

      const delta = this.lastTime ? Math.min(time - this.lastTime, 64) : 16;
      this.lastTime = time;
      const row = this.row;
      if (!row || !row.isConnected) return;

      if (this.hovering || time < this.pausedUntil) {
        this.position = row.scrollLeft;
        return;
      }

      if (!this.section.classList.contains('is-auto')) this.section.classList.add('is-auto');
      if (this.position == null || Math.abs(row.scrollLeft - this.position) > 2) this.position = row.scrollLeft;

      const max = row.scrollWidth - row.clientWidth;
      if (max <= 0) return;
      if (this.position >= max - 1) {
        // real end (all pages loaded): rest a moment, then glide back to the first card
        if (this.section.querySelector('.kl_reviews__load_more_button button')) return;
        this.holdAuto(2500);
        setTimeout(() => {
          row.scrollTo({ left: 0, behavior: 'smooth' });
          this.position = 0;
          this.holdAuto(1500);
        }, 2000);
        return;
      }

      this.position += (this.autoSpeed * delta) / 1000;
      row.scrollLeft = this.position;
    }

    // The row bleeds to the viewport edges with a padding of (viewport − content) / 2. scroll-padding can't express
    // that percentage relative to the parent, so mirror the computed padding – cards then snap to the content edge.
    syncSnapPadding() {
      const row = this.row;
      if (!row) return;
      const padding = getComputedStyle(row).paddingLeft;
      if (row.style.scrollPaddingInline !== padding) row.style.scrollPaddingInline = padding;
    }

    // arrows sit over the middle of the card row (the row's height changes when photos load)
    positionNav() {
      const row = this.row;
      if (!row || !this.nav) return;
      const top = row.getBoundingClientRect().top - this.section.getBoundingClientRect().top + row.offsetHeight / 2;
      this.nav.style.setProperty('--kl-nav-top', Math.round(top) + 'px');
      if (!this.navRowObserver) {
        this.navRowObserver = new ResizeObserver(() => this.positionNav());
      }
      if (this.navObservedRow !== row) {
        if (this.navObservedRow) this.navRowObserver.unobserve(this.navObservedRow);
        this.navRowObserver.observe(row);
        this.navObservedRow = row;
      }
    }

    updateCarousel() {
      const row = this.row;
      if (!row || !row.isConnected) return;
      const max = row.scrollWidth - row.clientWidth;

      if (this.nav) {
        this.nav.hidden = max <= 4;
        const prev = this.nav.querySelector('[data-kl-carousel-prev]');
        const next = this.nav.querySelector('[data-kl-carousel-next]');
        const loadMore = this.section.querySelector('.kl_reviews__load_more_button button');
        if (prev) prev.disabled = row.scrollLeft <= 4;
        if (next) next.disabled = row.scrollLeft >= max - 4 && !loadMore;
      }

      // near the end: let Klaviyo fetch the next page
      if (!this.loading && row.scrollLeft >= max - row.clientWidth * 1.2) {
        const loadMore = this.section.querySelector('.kl_reviews__load_more_button button');
        if (loadMore) {
          this.loading = true;
          clearTimeout(this.loadingTimer);
          this.loadingTimer = setTimeout(() => {
            this.loading = false;
          }, 5000);
          loadMore.click();
        }
      }
    }

    /* ---- Read more --------------------------------------------------------------------------------------------- */

    syncReadMore(wrapper) {
      wrapper = wrapper || this.section.querySelector(WRAPPER_SELECTOR);
      if (!wrapper) return;
      wrapper.querySelectorAll(ITEM_SELECTOR + ' p.kl_reviews__review__content').forEach((text) => {
        if (!this.observedTexts.has(text)) {
          this.observedTexts.add(text);
          this.textObserver.observe(text);
        }
        this.syncReadMoreFor(text);
      });
    }

    // Add / remove the toggle of one review card: only for texts that are really cut off (or already expanded).
    syncReadMoreFor(text) {
      const item = text.closest(ITEM_SELECTOR);
      const card = item && item.firstElementChild;
      if (!card || !card.contains(text)) return;

      const expanded = card.getAttribute('data-kl-expanded') === 'true';
      // clamped text: the content is taller than the box. Hidden / not laid out yet (0 height): leave it for later.
      const truncated = expanded || (text.clientHeight > 0 && text.scrollHeight - text.clientHeight > 1);
      let button = card.querySelector(':scope > .kl-review-toggle');

      if (!truncated) {
        if (button) button.remove();
        return;
      }

      if (!button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'kl-review-toggle';
        card.appendChild(button);
      }

      const label = expanded ? 'Weniger anzeigen' : 'Mehr anzeigen';
      if (button.textContent !== label) button.textContent = label;
      if (button.getAttribute('aria-expanded') !== String(expanded)) button.setAttribute('aria-expanded', String(expanded));
    }

    onReadMoreClick(event) {
      const button = event.target.closest && event.target.closest('.kl-review-toggle');
      if (!button || !this.section.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();

      const card = button.parentElement;
      const expanded = card.getAttribute('data-kl-expanded') === 'true';
      if (expanded) card.removeAttribute('data-kl-expanded');
      else card.setAttribute('data-kl-expanded', 'true');

      const text = card.querySelector('p.kl_reviews__review__content');
      if (text) this.syncReadMoreFor(text);
      if (this.isCarousel) this.holdAuto(8000);

      // collapsing a tall card from its bottom edge: bring the card back into view
      if (expanded) {
        const rect = card.getBoundingClientRect();
        if (rect.top < 0) card.scrollIntoView({ block: 'nearest' });
      }
    }

    // Klaviyo renders review photos as 300x300 thumbnails; the card shows the first photo at full card width, so
    // ask Klaviyo's image CDN (imgproxy style URL) for a larger 4:5 rendition instead of upscaling the thumbnail.
    upgradeCardImages(wrapper) {
      wrapper
        .querySelectorAll('.kl_reviews__review__image_row > :first-child img.kl_reviews__review__image')
        .forEach((img) => {
          const src = img.getAttribute('src') || '';
          if (!/\/width:300\/height:300\/plain\//.test(src)) return;
          img.setAttribute('src', src.replace('/width:300/height:300/plain/', '/width:800/height:1000/resizing_type:fill/plain/'));
        });
    }

    applyHistogramCounts(wrapper) {
      wrapper.querySelectorAll('.kl_reviews__histogram__row').forEach((row) => {
        const bar = row.querySelector('[aria-label]');
        const value = row.querySelector('.kl_reviews__histogram__value');
        if (!bar || !value) return;

        const label = (bar.getAttribute('aria-label') || '').replace(/ /g, ' ');
        const match = label.match(/\d[\d.,\s]*\d|\d/);
        if (!match) return;

        const count = '(' + match[0].replace(/\s+/g, '') + ')';
        if (value.getAttribute('data-kl-count') !== count) value.setAttribute('data-kl-count', count);
      });
    }
  }

  function init() {
    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      if (section.klaviyoReviewsGrid) return;
      section.klaviyoReviewsGrid = new KlaviyoReviewsGrid(section);
    });
  }

  installFetchHooks();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
